import type {Editor} from '../interfaces/editor'
import {Element} from '../interfaces/element'
import {Node, type Ancestor, type Descendant} from '../interfaces/node'
import type {Path} from '../interfaces/path'
import {Text} from '../interfaces/text'
import {Transforms} from '../interfaces/transforms'
import type {WithEditorFirstArg} from '../utils/types'

export const normalizeNode: WithEditorFirstArg<Editor['normalizeNode']> = (
  editor,
  entry,
) => {
  const [node, path] = entry as [object, Path]

  // There are no core normalizations for text nodes.
  if (Text.isText(node as Node, editor.schema)) {
    return
  }

  if (editor.isObjectNode(node)) {
    return
  }

  if (!('children' in node)) {
    // If the node is not a text node, and doesn't have a `children` field,
    // then we have an invalid node that will upset slate.
    //
    // eg: `{ type: 'some_node' }`.
    //
    // To prevent slate from breaking, we can add the `children` field,
    // and now that it is valid, we can to many more operations easily,
    // such as extend normalizers to fix erronous structure.
    ;(node as Element).children = []
  }

  // We will have to refetch the element any time we modify its children
  // since it clones to a new immutable reference when we do.
  let element = node as Ancestor

  // Ensure that elements have at least one child.
  if (element !== editor && element.children.length === 0) {
    const child = editor.createSpan()
    Transforms.insertNodes(editor, child, {at: path.concat(0), voids: true})
    element = Node.get(editor, path, editor.schema) as Element
  }

  // Determine whether the node should have only block or only inline children.
  // - The editor should have only block children.
  // - Inline elements should have only inline children.
  // - Elements that begin with a text child or an inline element child
  //   should have only inline children.
  // - All other elements should have only block children.
  const firstChild = element.children[0]!
  const shouldHaveInlines =
    !(element === editor) &&
    (editor.isInline(element) ||
      Text.isText(firstChild, editor.schema) ||
      editor.isObjectNode(firstChild) ||
      (Element.isElement(firstChild, editor.schema) &&
        editor.isInline(firstChild)))

  if (shouldHaveInlines) {
    // Since we'll be applying operations while iterating, we also modify
    // `n` when adding/removing nodes.
    for (let n = 0; n < element.children.length; n++) {
      const child = element.children[n] as Descendant
      const prev = element.children[n - 1] as Descendant | undefined

      if (Text.isText(child, editor.schema)) {
        if (prev != null && Text.isText(prev, editor.schema)) {
          // Merge adjacent text nodes that are empty or match.
          if (child.text === '') {
            Transforms.removeNodes(editor, {
              at: path.concat(n),
              voids: true,
            })
            element = Node.get(editor, path, editor.schema) as Element
            n--
          } else if (prev.text === '') {
            Transforms.removeNodes(editor, {
              at: path.concat(n - 1),
              voids: true,
            })
            element = Node.get(editor, path, editor.schema) as Element
            n--
          } else if (Text.equals(child, prev, {loose: true})) {
            Transforms.mergeNodes(editor, {at: path.concat(n), voids: true})
            element = Node.get(editor, path, editor.schema) as Element
            n--
          }
        }
      } else if (Element.isElement(child, editor.schema)) {
        if (editor.isInline(child)) {
          // Ensure that inline nodes are surrounded by text nodes.
          if (prev == null || !Text.isText(prev, editor.schema)) {
            const newChild = editor.createSpan()
            Transforms.insertNodes(editor, newChild, {
              at: path.concat(n),
              voids: true,
            })
            element = Node.get(editor, path, editor.schema) as Element
            n++
          }
          if (n === element.children.length - 1) {
            const newChild = editor.createSpan()
            Transforms.insertNodes(editor, newChild, {
              at: path.concat(n + 1),
              voids: true,
            })
            element = Node.get(editor, path, editor.schema) as Element
            n++
          }
        } else {
          // An Element cannot appear inline in another Element
          Transforms.removeNodes(editor, {at: path.concat(n), voids: true})
          element = Node.get(editor, path, editor.schema) as Element
          n--
        }
      } else if (editor.isObjectNode(child)) {
        if (prev == null || !Text.isText(prev, editor.schema)) {
          const newChild = editor.createSpan()
          Transforms.insertNodes(editor, newChild, {
            at: path.concat(n),
            voids: true,
          })
          element = Node.get(editor, path, editor.schema) as Element
          n++
        }
        if (n === element.children.length - 1) {
          const newChild = editor.createSpan()
          Transforms.insertNodes(editor, newChild, {
            at: path.concat(n + 1),
            voids: true,
          })
          element = Node.get(editor, path, editor.schema) as Element
          n++
        }
      }
    }
  } else {
    // Since we'll be applying operations while iterating, we also modify
    // `n` when adding/removing nodes.
    for (let n = 0; n < element.children.length; n++) {
      const child = element.children[n] as Descendant

      // Allow only block nodes in the top-level children and parent blocks
      // that only contain block nodes.
      if (
        Text.isText(child, editor.schema) ||
        (Element.isElement(child, editor.schema) && editor.isInline(child))
      ) {
        Transforms.removeNodes(editor, {at: path.concat(n), voids: true})
        element = Node.get(editor, path, editor.schema) as Ancestor
        n--
      }
    }
  }
}
